import React from 'react';
import Badge from '../ui/Badge';
import ActionButtons from './ActionButtons';

const panelStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-lg)',
    maxWidth: '600px',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.3rem',
    fontWeight: 700,
    color: 'var(--color-primary)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    margin: 0,
  },
  instruction: {
    padding: 'var(--space-md)',
    backgroundColor: 'rgba(52, 152, 219, 0.1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(52, 152, 219, 0.2)',
    fontFamily: 'var(--font-display)',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--color-info)',
    letterSpacing: '0.03em',
    textAlign: 'center',
  },
  offerAmount: {
    fontFamily: 'var(--font-display)',
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--color-primary)',
    textAlign: 'center',
    textShadow: '0 0 20px rgba(212, 175, 55, 0.3)',
    margin: 'var(--space-sm) 0',
  },
  offerLabel: {
    fontFamily: 'var(--font-display)',
    fontSize: '0.85rem',
    color: 'var(--color-text-muted)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    textAlign: 'center',
    margin: 0,
  },
  resultBox: {
    padding: 'var(--space-lg)',
    backgroundColor: 'rgba(212, 175, 55, 0.08)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.15)',
    textAlign: 'center',
  },
  resultLabel: {
    fontFamily: 'var(--font-display)',
    fontSize: '0.85rem',
    color: 'var(--color-text-muted)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    margin: '0 0 var(--space-sm) 0',
  },
  resultValue: {
    fontFamily: 'var(--font-display)',
    fontSize: '2.5rem',
    fontWeight: 700,
    color: 'var(--color-primary)',
    textShadow: '0 0 30px rgba(212, 175, 55, 0.4)',
    margin: 0,
  },
};

const statusMap = {
  setup: { label: 'Preparazione', variant: 'info' },
  playing: { label: 'In gioco', variant: 'success' },
  offer_pending: { label: 'Offerta in corso', variant: 'warning' },
  completed: { label: 'Completata', variant: 'default' },
  cancelled: { label: 'Annullata', variant: 'danger' },
};

function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  return value.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export default function ControlPanel({
  game,
  onMakeOffer,
  onRespondOffer,
  onCompleteGame,
  loading,
}) {
  if (!game) {
    return (
      <div style={panelStyles.container}>
        <div style={{
          padding: 'var(--space-lg)',
          backgroundColor: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid rgba(212, 175, 55, 0.1)',
        }}>
          <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', margin: 0 }}>
            Nessuna partita in corso. Crea una nuova partita per iniziare.
          </p>
        </div>
      </div>
    );
  }

  const status = statusMap[game.status] || { label: game.status, variant: 'default' };
  const isSetup = game.status === 'setup';
  const isPlaying = game.status === 'playing';
  const isOfferPending = game.status === 'offer_pending';
  const isCompleted = game.status === 'completed' || game.status === 'cancelled';

  return (
    <div style={panelStyles.container}>
      <div style={panelStyles.header}>
        <h2 style={panelStyles.title}>Pannello partita</h2>
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>

      {isSetup && !game.player_box_number && (
        <div style={panelStyles.instruction}>
          Seleziona il pacco del giocatore
        </div>
      )}

      {isSetup && game.player_box_number && (
        <div style={panelStyles.instruction}>
          Pacco {game.player_box_number} selezionato — in attesa di iniziare
        </div>
      )}

      {isOfferPending && game.last_offer && (
        <div style={{
          padding: 'var(--space-lg)',
          backgroundColor: 'rgba(243, 156, 18, 0.08)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid rgba(243, 156, 18, 0.2)',
        }}>
          {game.last_offer.swap_box_number ? (
            <>
              <p style={panelStyles.offerLabel}>Scambio proposto</p>
              <div style={{ ...panelStyles.offerAmount, fontSize: '1.5rem' }}>
                Pacco {game.last_offer.swap_box_number}
                {game.last_offer.swap_region_name && (
                  <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--color-text-muted)', fontWeight: 400 }}>
                    {game.last_offer.swap_region_name}
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              <p style={panelStyles.offerLabel}>Offerta attuale</p>
              <div style={panelStyles.offerAmount}>
                {formatEuro(game.last_offer.amount)}
              </div>
            </>
          )}
        </div>
      )}

      {isCompleted && (
        <div style={panelStyles.resultBox}>
          <p style={panelStyles.resultLabel}>Partita completata</p>
          {game.final_prize_value !== undefined && game.final_prize_value !== null && (
            <p style={panelStyles.resultValue}>
              {(() => {
                const playerBox = game.boxes?.find(b => b.is_player_box);
                if (playerBox?.prize_label && game.final_prize_value === 0) return playerBox.prize_label;
                return formatEuro(game.final_prize_value);
              })()}
            </p>
          )}
          {game.offer_accepted !== null && game.offer_accepted !== undefined && (
            <p style={{
              fontSize: '0.85rem',
              color: game.offer_accepted ? 'var(--color-success)' : 'var(--color-info)',
              margin: 'var(--space-sm) 0 0 0',
              fontFamily: 'var(--font-display)',
              letterSpacing: '0.05em',
            }}>
              {game.offer_accepted ? 'Offerta accettata' : 'Pacco aperto'}
            </p>
          )}
        </div>
      )}

      {!isCompleted && (
        <ActionButtons
          game={game}
          onMakeOffer={onMakeOffer}
          onRespondOffer={onRespondOffer}
          onCompleteGame={onCompleteGame}
          loading={loading}
        />
      )}
    </div>
  );
}
